import { afterNextRender, ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, signal, viewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmTypedAddressData } from '../modal-content';

/** Close enough to read the block, far enough to see the neighbourhood. */
const MAP_ZOOM = 16;

/**
 * Shows the address Google made of what the customer typed in the sign-up, next to the typed text
 * and on a map, so they can confirm it or go back and fix it (DEV-10744). The typed text is shown
 * for reference only: we can't deliver to an address Google could not place.
 */
@Component({
  selector: 'app-confirm-typed-address',
  imports: [],
  templateUrl: './confirm-typed-address.component.html',
  styleUrl: './confirm-typed-address.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConfirmTypedAddressComponent {
  private activeModal = inject(NgbActiveModal);

  data = input.required<ConfirmTypedAddressData>();

  mapContainer = viewChild<ElementRef<HTMLElement>>('map');
  /** The map is a nice-to-have: if Maps fails to load the window still works without it. */
  mapFailed = signal(false);

  mapsUrl = computed(() => {
    const { recommended, placeId } = this.data();
    const params = new URLSearchParams({ api: '1', query: recommended });
    if (placeId) params.set('query_place_id', placeId);
    return `https://www.google.com/maps/search/?${params}`;
  });

  constructor() {
    // Only opens in the browser, after the typed address was geocoded, so Maps is already loaded.
    afterNextRender(() => this.renderMap());
  }

  confirm() {
    this.activeModal.close({ confirm: true });
  }

  back() {
    this.activeModal.close({ confirm: false });
  }

  private async renderMap() {
    const container = this.mapContainer()?.nativeElement;
    if (!container) return;
    try {
      const { Map } = await google.maps.importLibrary('maps') as google.maps.MapsLibrary;
      // Fixed map: the pin is drawn by us at the centre, so it must not move or zoom.
      new Map(container, {
        center: this.data().location,
        zoom: MAP_ZOOM,
        disableDefaultUI: true,
        gestureHandling: 'none',
        keyboardShortcuts: false,
        clickableIcons: false,
      });
    } catch {
      this.mapFailed.set(true);
    }
  }
}
